import React, { useMemo, useState } from 'react';
import CascaDeModal from './shared/CascaDeModal';
import { useConfirm } from './shared/ConfirmDialog';
import { contractsService } from '../services/api';
import { esquecerRateio } from '../hooks/useRateio';
import { mensagemDoErroDeCarga } from '../design';
import { ContratoParaCota, PlanoDaEmpresa, RateioParaCota, planejarCotas } from '../utils/aplicarCotas';
import { TONE_BORDER, TONE_SURFACE, TONE_TEXT } from '../utils/statusColors';

/**
 * APLICAR AS COTAS NOS CONTRATOS (09/10/2026). Depois de gravar a divisão, o motor recusava todas as faturas do grupo
 * ("o valor do item deixou de ser a cota"): ele é neutro em valor e exige que cada contrato JÁ carregue a cota. A cota
 * era digitada à mão, assinatura por assinatura. Este modal mostra, empresa a empresa, o que muda em cada contrato e
 * grava num gesto só: a tela de rateio vira a única escritora da cota. As regras moram em `utils/aplicarCotas`.
 *
 * ⛔ Gravar relê ANTES: cada contrato é lido de novo e o plano refeito sobre o dado fresco. Se o contrato mudou desde a
 * tela (outro item, outro valor), aquela empresa não é gravada e a tela diz por quê.
 * ⛔ E relê DEPOIS: o `mrr` que vale é o que o servidor gravou (ele recalcula pela soma dos itens vigentes). Se divergir
 * do previsto, é aviso daquela empresa, não sucesso.
 */

const brl = (v: number) => `R$ ${Number(v || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const comSinal = (d: number) => `${d > 0 ? '+' : d < 0 ? '−' : ''}${brl(Math.abs(d))}`;
const th = 'text-[10px] uppercase tracking-wider text-slate-500 font-medium py-1';

type Resultado =
  | { clientId: number; nome: string; estado: 'aplicado'; mrrGravado: number; mrrPrevisto: number }
  | { clientId: number; nome: string; estado: 'falhou'; motivo: string };

const parseItens = (c: any) => (typeof c?.items === 'string' ? JSON.parse(c.items || '[]') : (c?.items || []));

export default function AplicarAsCotas({ rateio, contracts, nome, onClose, onAplicado }: {
  rateio: RateioParaCota;
  contracts: ContratoParaCota[];
  nome: (clientId: number) => string;
  onClose: () => void;
  onAplicado: () => void;
}) {
  const confirm = useConfirm();
  const plano = useMemo(() => planejarCotas(rateio, contracts, nome), [rateio, contracts, nome]);
  const aplicaveis = plano.filter((p): p is Extract<PlanoDaEmpresa, { situacao: 'aplica' }> => p.situacao === 'aplica');
  const [gravando, setGravando] = useState(false);
  const [resultado, setResultado] = useState<Resultado[] | null>(null);

  const aplicar = async () => {
    if (!aplicaveis.length) return;
    const total = aplicaveis.reduce((t, p) => t + p.diferenca, 0);
    if (!(await confirm({
      title: `Aplicar as cotas em ${aplicaveis.map((p) => p.nome).join(' e ')}?`,
      confirmLabel: 'Aplicar as cotas',
      consequence: `Os itens do que se divide passam a valer a cota de cada empresa (${aplicaveis.map((p) => `${p.nome} ${brl(p.cota)}`).join(', ')}). A soma das assinaturas muda ${comSinal(Math.round(total * 100) / 100)} por mês. Fatura já gerada não muda.`,
    }))) return;
    setGravando(true);
    const res: Resultado[] = [];
    for (const p of aplicaveis) {
      try {
        // ANTES: relê o contrato e refaz o plano sobre o dado fresco.
        const daEmpresa = await contractsService.getByClient(p.clientId);
        const fresco = (Array.isArray(daEmpresa) ? daEmpresa : []).map((c: any) => ({ ...c, items: parseItens(c) }));
        const refeito = planejarCotas(rateio, fresco, nome).find((x) => x.clientId === p.clientId);
        if (!refeito || refeito.situacao !== 'aplica' || refeito.contratoId !== p.contratoId || Math.round(refeito.diferenca * 100) !== Math.round(p.diferenca * 100)) {
          res.push({ clientId: p.clientId, nome: p.nome, estado: 'falhou', motivo: refeito?.situacao === 'recusa' ? refeito.motivo : 'A assinatura mudou desde que esta tela abriu. Nada foi gravado nela: feche e abra de novo.' });
          continue;
        }
        const gravado = await contractsService.update(refeito.contratoId, { items: refeito.itensNovos, mrr: refeito.mrrDepois });
        // DEPOIS: vale o mrr que o servidor gravou (a resposta do PUT é o contrato relido).
        const mrrGravado = Number(gravado?.mrr);
        res.push({ clientId: p.clientId, nome: p.nome, estado: 'aplicado', mrrGravado: Number.isFinite(mrrGravado) ? mrrGravado : NaN, mrrPrevisto: refeito.mrrDepois });
      } catch (e) {
        res.push({ clientId: p.clientId, nome: p.nome, estado: 'falhou', motivo: mensagemDoErroDeCarga(e) });
      }
    }
    esquecerRateio();
    setResultado(res);
    setGravando(false);
    if (res.some((r) => r.estado === 'aplicado')) onAplicado();
  };

  const recusas = plano.filter((p) => p.situacao === 'recusa');
  const jaBatem = plano.filter((p) => p.situacao === 'ja-bate');

  return (
    <CascaDeModal
      largura='lg'
      titulo='Aplicar as cotas nos contratos'
      subtitulo={`grupo de ${nome(rateio.matrizId)}`}
      onClose={onClose}
      corpoClassName='p-5 space-y-4'
      rodape={
        <div className='ml-auto flex gap-2'>
          <button type='button' onClick={onClose} className='px-4 py-2 rounded-lg bg-slate-700 hover:bg-slate-600 text-sm text-white'>{resultado ? 'Fechar' : 'Cancelar'}</button>
          {!resultado && (
            <button type='button' onClick={aplicar} disabled={gravando || aplicaveis.length === 0} className='px-4 py-2 rounded-lg bg-primary hover:bg-primary-strong text-sm font-medium text-white disabled:opacity-50'>
              {gravando ? 'Aplicando…' : 'Aplicar as cotas'}
            </button>
          )}
        </div>
      }
    >
      <p className='text-sm text-slate-400'>
        O motor só aplica a divisão numa fatura quando os itens do que se divide, no contrato, somam a cota da empresa. Aqui eles passam a somar: o total de cada assinatura muda pela diferença.
      </p>

      {resultado ? (
        <div className='space-y-2' data-testid='resultado-das-cotas'>
          {resultado.map((r) => (
            <div key={r.clientId} className={`rounded-lg border px-3 py-2 text-sm ${r.estado === 'aplicado' && Math.round(r.mrrGravado * 100) === Math.round(r.mrrPrevisto * 100) ? 'border-slate-700 text-slate-200' : `${TONE_BORDER.warning} ${TONE_SURFACE.warning} ${TONE_TEXT.warning}`}`} data-testid={`resultado-${r.clientId}`}>
              <span className='font-medium'>{r.nome}: </span>
              {r.estado === 'falhou'
                ? <>não aplicado. {r.motivo}</>
                : Math.round(r.mrrGravado * 100) === Math.round(r.mrrPrevisto * 100)
                  ? <>aplicado. O servidor gravou o MRR em {brl(r.mrrGravado)}.</>
                  : <>gravado, mas o servidor gravou o MRR em {Number.isFinite(r.mrrGravado) ? brl(r.mrrGravado) : '(não informado)'} e o previsto era {brl(r.mrrPrevisto)}. Confira a assinatura antes de ligar o motor.</>}
            </div>
          ))}
          <p className='text-xs text-slate-400'>Próximo passo: simular o motor no painel do grupo, e ligar se todas as faturas aplicarem.</p>
        </div>
      ) : (
        <>
          {aplicaveis.length > 0 && (
            <div className='overflow-x-auto' data-testid='plano-das-cotas'>
              <table className='w-full text-sm'>
                <thead><tr>
                  <th className={`${th} text-left`}>Empresa</th>
                  <th className={`${th} text-left`}>O que muda no contrato</th>
                  <th className={`${th} text-right`}>MRR antes → depois</th>
                  <th className={`${th} text-right`}>Por mês</th>
                </tr></thead>
                <tbody>
                  {aplicaveis.map((p) => (
                    <tr key={p.clientId} className='border-t border-central-borda align-top' data-testid={`plano-${p.clientId}`}>
                      <td className='py-1.5 text-slate-200'>{p.nome} <span className='text-[11px] text-slate-500 font-mono'>#{p.contratoId}</span></td>
                      <td className='py-1.5 text-slate-300'>
                        {p.mudancas.map((m, i) => (
                          <div key={i}>
                            {m.tipo === 'altera'
                              ? <>{m.produto}: {brl(m.de)} → <span className='text-slate-100'>{brl(m.para)}</span></>
                              : <>entra {m.produto}: <span className='text-slate-100'>{brl(m.para)}</span><span className='block text-[11px] text-slate-500'>{m.descricao}</span></>}
                          </div>
                        ))}
                      </td>
                      <td className='py-1.5 text-right tabular-nums text-slate-300'>{brl(p.mrrAntes)} → <span className='text-slate-100'>{brl(p.mrrDepois)}</span></td>
                      <td className={`py-1.5 text-right tabular-nums font-semibold ${p.diferenca < 0 ? TONE_TEXT.danger : p.diferenca > 0 ? TONE_TEXT.open : 'text-slate-400'}`}>{comSinal(p.diferenca)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          {jaBatem.length > 0 && (
            <p className='text-xs text-slate-400' data-testid='ja-batem'>Já carregam a cota, nada muda: {jaBatem.map((p) => p.nome).join(', ')}.</p>
          )}
          {recusas.length > 0 && (
            <div className={`rounded-lg border p-3 space-y-1 text-sm ${TONE_BORDER.warning} ${TONE_SURFACE.warning} ${TONE_TEXT.warning}`} data-testid='recusas-das-cotas'>
              <p className='font-medium'>Fica de fora (a tela não adivinha dinheiro):</p>
              {recusas.map((p) => <p key={p.clientId}>{p.nome}: {(p as any).motivo}</p>)}
            </div>
          )}
          {aplicaveis.length === 0 && <p className='text-sm text-slate-400' data-testid='nada-a-aplicar'>Nenhuma assinatura a mudar por aqui.</p>}
        </>
      )}
    </CascaDeModal>
  );
}
